import { cloneDeep, each, filter, findKey, reject } from 'lodash';
import {
  SET_ANALYSIS_ACTIVE_ABSTRACT_TYPE,
  SET_ANALYSIS_SIGNAL_DIMENSIONS_SORTING,
  SET_ANALYSIS_SIGNALS,
  SET_ANALYSIS_SIGNALS_COLORS,
  SET_ANALYSIS_SIGNALS_SORTING
} from './consts';
import { PROJECTS_TAG } from '../../../projects/data/actions';

const setProjectAnalysisSignals = signals => {
  return {
    type: SET_ANALYSIS_SIGNALS,
    signals
  };
};

export const setProjectAnalysisActiveAbstractType = (
  activeAbstractTypeId,
  activeAbstractType = null
) => {
  return {
    type: SET_ANALYSIS_ACTIVE_ABSTRACT_TYPE,
    activeAbstractTypeId,
    activeAbstractType
  };
};

export const setProjectAnalysisSignalsSorting = signalsSorting => {
  return {
    type: SET_ANALYSIS_SIGNALS_SORTING,
    signalsSorting
  };
};

export const setProjectAnalysisSignalDimensionsSorting = signalDimensionsSorting => {
  return {
    type: SET_ANALYSIS_SIGNAL_DIMENSIONS_SORTING,
    signalDimensionsSorting
  };
};

export const setProjectAnalysisSignalsColors = signalsColors => {
  return {
    type: SET_ANALYSIS_SIGNALS_COLORS,
    signalsColors
  };
};

/**
 Signals Sorting Order Actions
 * */
export const setSignalsSortingIds = signals => {
  return (dispatch, getState) => {
    const { signalsSorting } = getState().analysis.single;
    const { signalDimensionsSorting } = getState().analysis.single;
    const { layers } = getState().analysis.single;

    const rejected = reject(layers, l => {
      return l.id in signalsSorting;
    }).map(layer => {
      return layer.id;
    });

    each(rejected, layer => {
      signalsSorting[layer] = signals.map(signal => {
        return signal.id;
      });
    });

    dispatch(setProjectAnalysisSignalsSorting(signalsSorting));

    // Set Dimensions

    const rejectedForDimensions = reject(layers, l => {
      return l.id in signalDimensionsSorting;
    }).map(layer => {
      return layer.id;
    });

    each(rejectedForDimensions, layer => {
      signalDimensionsSorting[layer] = {};
      each(signals, signal => {
        signalDimensionsSorting[layer][signal.id] = filter(signal.dimensions, {
          is_analysable: true
        }).map(dimension => {
          return dimension.abstract_type_id;
        });
      });
    });

    dispatch(setProjectAnalysisSignalDimensionsSorting(signalDimensionsSorting));
  };
};

export const fetchProjectAnalysisSignals = projectId => {
  return async (dispatch, getState, { apiClient }) => {
    const results = await apiClient.call(PROJECTS_TAG, 'getProjectSignals', {
      projectId,
      $limit: 100,
      $extend: ['dimensions']
    });
    dispatch(setProjectAnalysisSignals(results.data));
    dispatch(setSignalsSortingIds(results.data));
  };
};

export const setProjectAnalysisSignalColorsFromLayers = layers => {
  return async (dispatch, getState) => {
    const layerSignalColors = cloneDeep(getState().analysis.single.signalsColors);

    each(layers, layer => {
      if (!layerSignalColors[layer.id]) layerSignalColors[layer.id] = {};
      each(layer.layer_abstract_type_associations, abstractType => {
        if (!layerSignalColors[layer.id][abstractType.abstract_type_id])
          layerSignalColors[layer.id][abstractType.abstract_type_id] = {
            color: [abstractType.color_r, abstractType.color_g, abstractType.color_b],
            opacity: 0.5
          };

        layerSignalColors[layer.id][abstractType.abstract_type_id].color = [
          abstractType.color_r,
          abstractType.color_g,
          abstractType.color_b
        ];
      });
    });
    dispatch(setProjectAnalysisSignalsColors(layerSignalColors));
  };
};

export const setOpacityForLayerSignalColor = (layerId, abstractTypeId, inOpacity) => {
  const opacity = inOpacity / 100;
  return async (dispatch, getState) => {
    const layerSignalColors = cloneDeep(getState().analysis.single.signalsColors);
    if (!layerSignalColors[layerId]) layerSignalColors[layerId] = {};
    if (!layerSignalColors[layerId][abstractTypeId])
      layerSignalColors[layerId][abstractTypeId] = {
        opacity
      };

    layerSignalColors[layerId][abstractTypeId] = {
      ...layerSignalColors[layerId][abstractTypeId],
      opacity
    };

    dispatch(setProjectAnalysisSignalsColors(layerSignalColors));
  };
};

const moveAbstractTypeElement = (direction, list, idToMove) => {
  const elementIndex = list.indexOf(idToMove);

  if (
    (direction === 'up' && elementIndex === 0) ||
    (direction === 'down' && elementIndex === list.length - 1)
  ) {
    return list;
  }

  const itemToSwap = list[elementIndex];
  const toIndex = direction === 'up' ? elementIndex - 1 : elementIndex + 1;

  list[elementIndex] = list[toIndex];
  list[toIndex] = itemToSwap;

  return list;
};

export const abstractTypesSortingHandler = direction => {
  return (dispatch, getState) => {
    const { activeLayerId } = getState().analysis.single;
    const { activeAbstractType } = getState().analysis.single;
    const { activeAbstractTypeId } = getState().analysis.single;

    if (activeLayerId === null || activeAbstractTypeId === null) {
      return;
    }

    if (activeAbstractType === 'signal') {
      const layerAbstractTypesSorting = cloneDeep(getState().analysis.single.signalsSorting);
      let activeLayerSignalsSorting = layerAbstractTypesSorting[activeLayerId].slice();

      activeLayerSignalsSorting = moveAbstractTypeElement(
        direction,
        activeLayerSignalsSorting,
        activeAbstractTypeId
      );

      dispatch(
        setProjectAnalysisSignalsSorting({
          ...layerAbstractTypesSorting,
          [activeLayerId]: activeLayerSignalsSorting
        })
      );
    } else {
      const layerSignalDimensionsSorting = cloneDeep(
        getState().analysis.single.signalDimensionsSorting
      );
      const activeDimensionsSignalSortingId = findKey(
        layerSignalDimensionsSorting[activeLayerId],
        signalGrouping => {
          return signalGrouping.indexOf(activeAbstractTypeId) >= 0;
        }
      );

      let activeLayerSignalDimensionsSorting = layerSignalDimensionsSorting[activeLayerId][
        activeDimensionsSignalSortingId
      ].slice();

      activeLayerSignalDimensionsSorting = moveAbstractTypeElement(
        direction,
        activeLayerSignalDimensionsSorting,
        activeAbstractTypeId
      );

      dispatch(
        setProjectAnalysisSignalDimensionsSorting({
          ...layerSignalDimensionsSorting,
          [activeLayerId]: {
            ...layerSignalDimensionsSorting[activeLayerId],
            [activeDimensionsSignalSortingId]: activeLayerSignalDimensionsSorting
          }
        })
      );
    }
  };
};



// WEBPACK FOOTER //
// ./src/scenes/analysis/data/analysis/signalsActions.js